/**
 * Design tokens — single source of truth for colors, spacing, typography.
 *
 * CP5 (docs/for-developers/ARCHITECTURE_CONTRACT.md § Code Portability Contract):
 *   Every style value under `apps/**` must resolve from here. Hex literals
 *   and raw `px` values outside this directory fail CI (stylelint +
 *   `scripts/token-scan.mjs`).
 *
 * Ported from legacy `frontend/src/App.css` `:root { ... }` block (the
 * legacy mobile-first CSS custom properties). Palette pulled from the
 * legacy header gradient + accent colors.
 *
 * Consumers:
 *   - Primitives and screens in `apps/web/src/` reference these either as
 *     CSS variables via `var(--color-...)` (preferred, see `tokens.css`
 *     injected in `main.tsx`) or by importing `tokens` directly when JSX
 *     inline styles are unavoidable.
 *   - Native app (future) imports `tokens` and feeds the scale into a
 *     StyleSheet factory or ThemeProvider.
 *
 * Structure: grouped by category so token names read `category.variant`
 * (e.g. `tokens.color.bg.primary`). Intentionally narrow — under 30
 * semantic slots, not a utility library.
 */

// ---------------------------------------------------------------------------
// Palette — raw hex values that semantic tokens reference. Private to this
// file; downstream consumers never see hex directly.
// ---------------------------------------------------------------------------

const palette = {
  white: '#ffffff',
  black: '#000000',

  // Grays (Tailwind-ish, from legacy --color-gray-* + manual additions)
  gray50: '#f8fafc',
  gray100: '#f1f5f9',
  gray200: '#e5e7eb',
  gray300: '#d1d5db',
  gray500: '#6b7280',
  gray700: '#374151',
  gray900: '#111827',

  // Accent (legacy --color-primary + header gradient stops).
  // T47 Phase 3 — violet→indigo palette migration: the legacy accent stops
  // (#2563eb / #7c3aed / #a855f7 / #ec4899) are reskinned to the settled
  // indigo brand (Figma 148-4516). text.accent links now read indigo too.
  blue600: '#525edc',
  violet600: '#525edc',
  purple500: '#8075ff',
  pink500: '#fe5875',

  // Status
  green600: '#16a34a',
  amber500: '#f59e0b',
  red600: '#dc2626',

  // Tints (T12.2 step 11a) — pale accent/success/danger surface fills. The
  // prototype renders tinted surfaces (the 🧭 band, on-tags, like/avoid chips,
  // the selected eating-style card, the NEW badge, the active sidebar link) on
  // these pales. Without dedicated slots they fell back to gray
  // (`--color-bg-muted`) and the design's purple/mint/rose language was lost.
  // T47 Phase 3 — pale indigo tint (was pale violet #f5f3ff).
  violet50: '#eef0fe',
  mint50: '#ecfdf3',
  rose50: '#fef2f2',

  // T19 Stage 5 — brand palette stops, reskinned in T47 Phase 3 from the
  // legacy violet→pink gradient (#7c3aed → #a855f7 → #ec4899) to the settled
  // indigo brand (#525edc → #8075ff → #fe5875, Figma 148-4516). Held as palette
  // entries so semantic tokens and the `--gradient-brand` shorthand can both
  // reference them without duplicating the literal values. `brandGreen` is
  // unchanged — green is the savings/semantic color + the logo check, not part
  // of the violet→indigo move.
  // NOTE (redundancy, consolidate later): after the T47 swap `brandPurple`
  // (#525edc) now equals `brandIndigo` below. A later cleanup should collapse
  // the two; left distinct here to avoid churning consumers in this pass.
  brandPurple: '#525edc',
  brandPurpleLight: '#8075ff',
  brandPink: '#fe5875',
  brandGreen: '#10b981',

  // T47 — new indigo basket logo. The settled brand mark is a flat indigo
  // basket; the `<Logo>`/`<LogoIcon>` primitives bind their basket fill to
  // this. Theme-invariant (reads on both light and dark), so it sits in the
  // brand block alongside the legacy stops and is NOT overridden on dark. The
  // full violet→indigo palette swap stays deferred to the brand-kit migration;
  // this is only the one token the logo geometry needs.
  brandIndigo: '#525EDC',

  // T12.2 step 10a — dark-theme palette. Authored values (not inverted): a
  // near-black slate ladder for surfaces, light slate text, and brightened
  // accent / status / tint stops chosen so the brand stays recognizable and
  // body text + the 🧭 steers band clear AA contrast on dark.
  //   slate ladder (surfaces → borders → text):
  darkBg900: '#0f1117', // app background (deepest)
  darkBg800: '#161922', // page / chrome surface (≈ proto page #13161f)
  darkBg700: '#1d212c', // muted fills / sunken
  // Lifted card surface — a step LIGHTER than the page so cards read as raised
  // (proto card value, feedback-dark.html dark :root `--bg`). Page surfaces sit
  // a step darker than cards → cards read elevated; chrome (header/nav) stays at
  // page level. darkText100 (#e8eaf0) on this clears AA.
  darkBgRaised: '#1e2330', // raised cards (lifted above the page)
  darkBorder600: '#2f3645', // subtle hairline on dark (proto `--border`)
  darkBorder500: '#3a4150', // strong border on dark
  darkText100: '#e8eaf0', // primary text (AA on darkBg800/900)
  darkText300: '#b6bcc9', // secondary text
  darkText500: '#8b93a4', // muted text (AA on darkBg900)
  //   accent — brightened indigo/magenta so it reads on dark and stays the
  //   settled "Shopista indigo" (T47 Phase 3; brightened for AA on dark, each
  //   contrast-checked vs darkBg800/900 — all clear AA 4.5:1):
  darkAccent: '#8f9cff', // accent primary on dark (AA: 6.99 vs bg800 / 7.52 vs bg900)
  darkAccent2: '#7c8cf8', // accent secondary (AA: 5.82 vs bg800 / 6.26 vs bg900)
  darkAccent3: '#ff8298', // accent tertiary / magenta (AA: 7.44 vs bg800 / 8.00 vs bg900)
  //   status — brightened so they pass AA on the dark surface:
  darkGreen: '#4ade80',
  darkAmber: '#fbbf24',
  darkRed: '#f87171',
  //   tints — deep translucent-feeling fills (solid hex) under accent/like/avoid
  //   surfaces, dark enough that the brightened accent/status text clears AA:
  darkAccentSubtle: '#222746', // indigo tint on dark (T47 Phase 3; subtle fill)
  darkSuccessSubtle: '#16301f', // mint tint on dark
  darkDangerSubtle: '#3a1d1d', // rose tint on dark

  // T27.2.5.3 (B3) — modal scrim. The bottom-sheet backdrop dims the app behind
  // an open sheet. Held as palette entries (translucent rgba, not solid hex) so
  // the semantic `overlay.scrim` token can theme without duplicating the rgba.
  //   light — the legacy `rgba(17, 24, 39, 0.4)` (gray900 @ 40%): a soft dark-
  //   blue tint over the bright app.
  scrimLight: 'rgba(17, 24, 39, 0.4)',
  //   dark — heavier (pure black @ 60%) so a scrim over the already-dark app
  //   still reads as "the sheet is on top". A dark tint at 40% gave almost no
  //   separation cue on dark (the finding); black @ 60% restores it.
  scrimDark: 'rgba(0, 0, 0, 0.6)',
} as const;

// ---------------------------------------------------------------------------
// Semantic color tokens — what components consume.
// ---------------------------------------------------------------------------

const color = {
  bg: {
    primary: palette.white,
    surface: palette.gray50,
    raised: palette.white,
    muted: palette.gray100,
    inverse: palette.gray900,
  },
  text: {
    primary: palette.gray900,
    secondary: palette.gray700,
    muted: palette.gray500,
    inverse: palette.white,
    accent: palette.blue600,
  },
  border: {
    subtle: palette.gray200,
    strong: palette.gray300,
    focus: palette.brandPurple,
  },
  accent: {
    primary: palette.brandPurple,
    secondary: palette.violet600,
    tertiary: palette.pink500,
    // Pale violet tint behind accent surfaces (steers band, on-tags, NEW
    // badge, selected eating-style card, active sidebar link). T12.2 step 11a.
    subtle: palette.violet50,
  },
  status: {
    success: palette.green600,
    warning: palette.amber500,
    danger: palette.red600,
    // Pale mint / rose tints behind like / avoid chips. T12.2 step 11a.
    successSubtle: palette.mint50,
    dangerSubtle: palette.rose50,
  },
  // Overlay scrims (T27.2.5.3 / B3) — the dimming layer behind a modal /
  // bottom sheet. `scrim` is the bottom-sheet backdrop; consumed by AccountSheet
  // + CaptureFab in place of the prior `rgba(17,24,39,0.4)` literal. Themed via
  // the dark override below so a dark scrim still separates the sheet on dark.
  overlay: {
    scrim: palette.scrimLight,
  },
  // T19 Stage 5 — brand identity colors. Distinct from the more
  // generic `accent` slots (which reference blue/violet/pink at
  // different roles) — these read explicitly as the legacy
  // Shopista brand and drive the `<Logo>` SVG + the gradient
  // wordmark + the auth backdrop.
  brand: {
    purple: palette.brandPurple,
    purpleLight: palette.brandPurpleLight,
    pink: palette.brandPink,
    green: palette.brandGreen,
    // T47 — new indigo basket logo fill. Theme-invariant (see dark mirror).
    indigo: palette.brandIndigo,
  },
} as const;

// ---------------------------------------------------------------------------
// Dark theme — the dark-mode override of the semantic color tokens (T12.2
// step 10a). Same shape as `color` so `generateThemeCssVars` can emit a
// `[data-theme="dark"] { … }` block with every `--color-*` var redefined.
// Because the whole app is token-driven (CP5), redefining these cascades the
// dark palette across every screen — no per-component dark styling needed.
//
// The brand identity colors (`brand.*`, and the gradient that references them)
// are intentionally NOT overridden — the wordmark / logo keep their fixed brand
// stops in both themes.
// ---------------------------------------------------------------------------

// Structural shape of the semantic color map — every leaf is a string. Used to
// type `darkColor` so it mirrors `color`'s slots without inheriting the light
// values' literal types (which would reject any dark value).
type ColorMap = {
  [G in keyof typeof color]: { [V in keyof (typeof color)[G]]: string };
};

const darkColor: ColorMap = {
  bg: {
    primary: palette.darkBg800,
    surface: palette.darkBg900,
    // Cards lift a step LIGHTER than the page (proto card surface). Page chrome
    // (header/nav) repoints to `bg-primary` so it stays at page level — see the
    // `--color-bg-raised` consumer audit; in light, primary==raised==white so
    // that repoint is a no-op.
    raised: palette.darkBgRaised,
    muted: palette.darkBg700,
    inverse: palette.darkText100,
  },
  text: {
    primary: palette.darkText100,
    secondary: palette.darkText300,
    muted: palette.darkText500,
    inverse: palette.darkBg900,
    accent: palette.darkAccent,
  },
  border: {
    subtle: palette.darkBorder600,
    strong: palette.darkBorder500,
    focus: palette.darkAccent,
  },
  accent: {
    primary: palette.darkAccent,
    secondary: palette.darkAccent2,
    tertiary: palette.darkAccent3,
    subtle: palette.darkAccentSubtle,
  },
  status: {
    success: palette.darkGreen,
    warning: palette.darkAmber,
    danger: palette.darkRed,
    successSubtle: palette.darkSuccessSubtle,
    dangerSubtle: palette.darkDangerSubtle,
  },
  // Heavier scrim on dark so the sheet still reads as "on top" over the dark
  // app (T27.2.5.3 / B3). A 40% dark-blue tint gave almost no cue on dark.
  overlay: {
    scrim: palette.scrimDark,
  },
  // Brand stops are theme-invariant — the logo / wordmark gradient keeps its
  // fixed identity in dark mode.
  brand: {
    purple: palette.brandPurple,
    purpleLight: palette.brandPurpleLight,
    pink: palette.brandPink,
    green: palette.brandGreen,
    // Theme-invariant — the indigo basket reads on both light and dark, so the
    // dark mirror holds the same value (T47).
    indigo: palette.brandIndigo,
  },
};

// ---------------------------------------------------------------------------
// Spacing — 4px scale. Legacy used 0.25rem / 0.5rem / 0.75rem / 1rem / 1.5rem
// / 2rem. We keep the same six steps with shorter names.
// ---------------------------------------------------------------------------

const space = {
  '0': '0',
  '1': '0.25rem', // 4px
  '2': '0.5rem', // 8px
  '3': '0.75rem', // 12px
  // T12.1.5.1.7 — list-row padding tiers. The planning/shopping list rows are
  // hand-tuned at 13px/14px vertical padding, which the 4px scale lacks (12 or
  // 16). Added additively so only the opted-in list styles use them. Keys avoid
  // a decimal point so the emitted `--space-*` var names stay clean idents.
  '3h': '0.8125rem', // 13px — planning row vertical padding (½-step above space-3)
  '3q': '0.875rem', // 14px — planning row horizontal + shopping check padding (¾-step)
  '4': '1rem', // 16px
  '6': '1.5rem', // 24px
  '8': '2rem', // 32px
  '12': '3rem', // 48px
} as const;

// ---------------------------------------------------------------------------
// Typography — font stack + rem-based scale. No raw px.
// ---------------------------------------------------------------------------

const font = {
  family: {
    // T47 Phase 4 — Poppins/Inter brand type. `display` (Poppins) drives
    // headings; `sans` (Inter) drives body + controls. Both lead the brand
    // face then fall back to the system stack, so a screen still reads if the
    // self-hosted face fails to load. The faces are loaded at the app entry
    // (`main.tsx` imports `@fontsource/poppins` + `@fontsource/inter`) — a bare
    // family name with no `@fontsource` import would silently fall through to
    // the system font. The emitter produces `--font-family-display`,
    // `--font-family-sans`, `--font-family-mono` from these keys.
    display: `'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', sans-serif`,
    sans: `'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', sans-serif`,
    mono: `ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace`,
  },
  size: {
    // T12.1.5.1.7 — fine sub-`xs`/sub-`base` tiers the planning + shopping list
    // surfaces are hand-tuned at. The 6-step ramp (xs 13 / sm 14 / base 16)
    // rounds the prototype's half-px tiers UP — brand line 12.5→14, status chip
    // 11.5→14, item name 15→16, section head 12→13 — reading 1–2px oversized.
    // Added additively (existing slots untouched) so only the opted-in list
    // styles move; the rest of the app is unaffected.
    '2xs': '0.71875rem', // 11.5px — status chip, flyer badge
    '3xs': '0.75rem', // 12px — section head, store-head count, nudge subtext
    '2sm': '0.78125rem', // 12.5px — brand line, more-brands, picked line
    xs: '0.8125rem', // 13px — meta, badges
    sm: '0.875rem', // 14px — labels
    smPlus: '0.84375rem', // 13.5px — qty label, store name in availability
    base15: '0.9375rem', // 15px — planning item name, shopping check label
    base: '1rem', // 16px — body (iOS-safe)
    md: '1.125rem', // 18px — emphasis
    lg: '1.25rem', // 20px — headings
    xl: '1.5rem', // 24px — page titles
  },
  weight: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  lineHeight: {
    tight: 1.25,
    normal: 1.5,
    relaxed: 1.75,
  },
} as const;

// ---------------------------------------------------------------------------
// Radius, shadow, z-index
// ---------------------------------------------------------------------------

const radius = {
  sm: '0.25rem', // 4px
  md: '0.375rem', // 6px
  lg: '0.5rem', // 8px
  pill: '9999rem',
} as const;

// Border widths (T30). One blessed hairline form so the scanner's tolerated
// `1px` literal is never the form devs reach for — `var(--border-width-hairline)`
// reads from here, matching the `0.0625rem` documented in frontend-standards §2.
const borderWidth = {
  hairline: '0.0625rem', // 1px at 16px root — device hairline / 1px borders
} as const;

const shadow = {
  sm: '0 1px 2px rgba(0, 0, 0, 0.05)',
  md: '0 4px 8px rgba(0, 0, 0, 0.08)',
  lg: '0 10px 24px rgba(0, 0, 0, 0.12)',
} as const;

const zIndex = {
  base: 0,
  raised: 1,
  // A pinned-banner tier (the Feedback "support development" banner): above
  // scrolling content but BELOW the fixed bottom nav / header (`sticky`, 100),
  // so the banner sits under the nav rather than over it.
  banner: 50,
  sticky: 100,
  modal: 1000,
  toast: 2000,
} as const;

// ---------------------------------------------------------------------------
// Gradient tokens (T19 Stage 5)
//
// The legacy Shopista app uses a single 3-stop diagonal gradient as
// its brand mark (logo circle, wordmark text, login backdrop). We
// expose two variants:
//
//   --gradient-brand           the canonical 3-stop linear gradient
//                              used inside the Logo SVG and as the
//                              text-clip background for the wordmark.
//   --gradient-brand-backdrop  a softer diagonal split for the login
//                              page (white top-left → brand pink
//                              bottom-right) that keeps the card
//                              legible without animated stripe waves.
//
// Both stay in tokens land so any consumer (CSS / inline style /
// future native theming) reads from one source.
// ---------------------------------------------------------------------------

const gradient = {
  brand: `linear-gradient(135deg, ${palette.brandPurple} 0%, ${palette.brandPurpleLight} 40%, ${palette.brandPink} 100%)`,
  brandBackdrop: `linear-gradient(135deg, ${palette.white} 0%, ${palette.gray50} 35%, ${palette.brandPurpleLight} 80%, ${palette.brandPink} 100%)`,
} as const;

// T27.2.5.4 (G1) — dark-mode gradient overrides. The login backdrop
// (`--gradient-brand-backdrop`) is the only gradient that follows the theme:
// in light mode it is a bright white→lilac→pink wash; in dark mode a dark-mode
// user would otherwise hit that glaringly-bright login before the app flips
// dark after sign-in (the finding). Here we redefine ONLY the backdrop to a
// calm dark wash composed from the dark palette (darkBg900 → a muted
// brand-purple tint → darkBg800), so login matches the dark app it leads into.
//
// The brand LOGO gradient (`--gradient-brand`) is intentionally NOT overridden —
// the logo / wordmark keep their fixed brand stops in both themes (same rule as
// the brand color stops above). Only consumers that read
// `--gradient-brand-backdrop` change under dark; `generateDarkThemeBlock` emits
// just this one gradient line into the `[data-theme="dark"]` block.
const darkGradient = {
  brandBackdrop: `linear-gradient(135deg, ${palette.darkBg900} 0%, ${palette.darkAccentSubtle} 60%, ${palette.darkBg800} 100%)`,
} as const;

// ---------------------------------------------------------------------------
// Public token bundle — the sole export shape consumers import.
// ---------------------------------------------------------------------------

export const tokens = {
  color,
  space,
  font,
  radius,
  shadow,
  zIndex,
  gradient,
  borderWidth,
} as const;

export type Tokens = typeof tokens;

// ---------------------------------------------------------------------------
// CSS variable generator — apps emit this string into a <style> tag at
// bootstrap so components can reference tokens via `var(--color-bg-primary)`.
// Naming: kebab-case, group-prefixed. Numeric keys in `space` become
// `--space-1` through `--space-12`. Keep in sync with consumers.
// ---------------------------------------------------------------------------

/**
 * Generate the CSS custom-property declarations for the full token set.
 * Returns the body of a `:root { ... }` block without the braces, so
 * callers can wrap it however they need.
 */
/**
 * Emit the `--color-{group}-{variant}: …;` declarations for a color map.
 * Variant names are kebab-cased so camelCase keys (e.g. brand.purpleLight)
 * emit as `--color-brand-purple-light`. Shared by the `:root` (light) and the
 * `[data-theme="dark"]` override block so both stay in sync.
 */
function colorVarLines(colorMap: ColorMap): string[] {
  const lines: string[] = [];
  for (const [group, variants] of Object.entries(colorMap)) {
    for (const [variant, value] of Object.entries(variants)) {
      const variantKebab = variant.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);
      lines.push(`  --color-${group}-${variantKebab}: ${value};`);
    }
  }
  return lines;
}

export function generateCssVars(): string {
  const lines: string[] = [];

  // Colors: --color-{group}-{variant}
  lines.push(...colorVarLines(color));

  // Spacing: --space-{step}
  for (const [step, value] of Object.entries(space)) {
    lines.push(`  --space-${step}: ${value};`);
  }

  // Font family
  for (const [key, value] of Object.entries(font.family)) {
    lines.push(`  --font-family-${key}: ${value};`);
  }
  // Font sizes
  for (const [key, value] of Object.entries(font.size)) {
    lines.push(`  --font-size-${key}: ${value};`);
  }
  // Font weights
  for (const [key, value] of Object.entries(font.weight)) {
    lines.push(`  --font-weight-${key}: ${value};`);
  }
  // Line height
  for (const [key, value] of Object.entries(font.lineHeight)) {
    lines.push(`  --line-height-${key}: ${value};`);
  }

  // Radius
  for (const [key, value] of Object.entries(radius)) {
    lines.push(`  --radius-${key}: ${value};`);
  }
  // Shadow
  for (const [key, value] of Object.entries(shadow)) {
    lines.push(`  --shadow-${key}: ${value};`);
  }
  // Z-index
  for (const [key, value] of Object.entries(zIndex)) {
    lines.push(`  --z-${key}: ${String(value)};`);
  }

  // Border widths: --border-width-{key}
  for (const [key, value] of Object.entries(borderWidth)) {
    lines.push(`  --border-width-${key}: ${value};`);
  }

  // Gradient tokens (T19 Stage 5).
  // Naming: `--gradient-{key}` with kebab-case for camelCase keys
  // (`brandBackdrop` → `brand-backdrop`). Mirrors the colon-grouped
  // shape of the color tokens but lives in its own namespace so
  // consumers can find it independently of `--color-*`.
  for (const [key, value] of Object.entries(gradient)) {
    const kebab = key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);
    lines.push(`  --gradient-${kebab}: ${value};`);
  }

  return lines.join('\n');
}

/**
 * Wrap `generateCssVars()` output in a `:root { ... }` block. Convenience
 * for apps that inject a complete stylesheet.
 */
export function generateCssRootBlock(): string {
  return `:root {\n${generateCssVars()}\n}\n`;
}

// ---------------------------------------------------------------------------
// Dark theme emit (T12.2 step 10a)
//
// The dark palette redefines only the `--color-*` vars (spacing / type /
// radius / etc. are theme-invariant). Callers inject this `[data-theme="dark"]`
// block alongside the `:root` block; the theme provider sets `data-theme` on
// `<html>` to activate it. Because the app is fully token-driven (CP5), the
// var override cascades the dark palette across every screen.
// ---------------------------------------------------------------------------

/**
 * The dark-mode `--color-*` declarations (body only, no selector/braces).
 */
export function generateDarkColorVars(): string {
  return colorVarLines(darkColor).join('\n');
}

/**
 * Emit the `--gradient-{key}: …;` declarations a dark gradient map overrides.
 * Mirrors the kebab-casing the light gradient emit uses in `generateCssVars`,
 * so a dark `brandBackdrop` redefines `--gradient-brand-backdrop` exactly
 * (T27.2.5.4 / G1). Only the gradients present in `darkGradient` are emitted —
 * the brand logo gradient is absent there, so it keeps its light/invariant value.
 */
function gradientVarLines(gradientMap: Record<string, string>): string[] {
  return Object.entries(gradientMap).map(([key, value]) => {
    const kebab = key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);
    return `  --gradient-${kebab}: ${value};`;
  });
}

/**
 * The dark-mode gradient overrides (body only). Currently just the login
 * backdrop — the one gradient that follows the theme (T27.2.5.4 / G1).
 */
export function generateDarkGradientVars(): string {
  return gradientVarLines(darkGradient).join('\n');
}

/**
 * Wrap the dark color + gradient vars in a `[data-theme="dark"] { … }` block.
 * Apps inject this next to the `:root` token block so toggling
 * `<html data-theme="dark">` swaps the palette app-wide. The color vars carry
 * the authored dark palette; the gradient vars redefine the login backdrop so
 * it matches the dark app (T27.2.5.4 / G1) — the brand logo gradient is left
 * out of the dark map, so it stays theme-invariant.
 */
export function generateDarkThemeBlock(): string {
  return `[data-theme="dark"] {\n${generateDarkColorVars()}\n${generateDarkGradientVars()}\n}\n`;
}
